import {
  adminContactSchema,
  adminContactSummarySchema,
  CONTACT_NOTE_MAX,
  CONTACT_REPLY_MAX,
  CONTACT_STATUS_LABELS,
  CONTACT_SUBJECT_LABELS,
  contactStatusSchema,
  DIRECTION_REASON_LABELS,
  DIRECTION_SUBJECT,
  contactSubjectSchema,
  type AdminContact,
  type AdminContactSummary,
  type ContactStatus,
  type ContactSubject,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  Field,
  Notice,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type Column,
  type StatusTone,
} from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

const STATUS_TONE: Record<ContactStatus, StatusTone> = {
  new: 'info',
  handled: 'success',
  archived: 'neutral',
};

function StatusOf({ contact }: { contact: AdminContactSummary }) {
  return (
    <span className="flex flex-wrap justify-end gap-1">
      {contact.mailedAt === null ? <StatusBadge tone="warning">Email falhou</StatusBadge> : null}
      <StatusBadge tone={STATUS_TONE[contact.status]}>
        {CONTACT_STATUS_LABELS[contact.status]}
      </StatusBadge>
    </span>
  );
}

const COLUMNS: Column<AdminContactSummary>[] = [
  { key: 'createdAt', header: 'Recebida', cell: (c) => formatDateTime(c.createdAt) },
  {
    key: 'from',
    header: 'De',
    cell: (c) => (
      <span className="flex flex-col">
        <span className="font-semibold">{c.name}</span>
        <span className="text-caption text-gu-ink-muted">{c.email}</span>
      </span>
    ),
  },
  {
    key: 'subject',
    header: 'Assunto',
    cell: (c) => (
      <span className="flex flex-col">
        <span>{CONTACT_SUBJECT_LABELS[c.subject]}</span>
        {c.replyCount > 0 ? (
          <span className="text-caption text-gu-ink-muted">
            {c.replyCount === 1 ? '1 resposta' : `${c.replyCount} respostas`}
          </span>
        ) : null}
        {c.noteCount > 0 ? (
          <span className="text-caption text-gu-ink-muted">
            {c.noteCount === 1 ? '1 nota interna' : `${c.noteCount} notas internas`}
          </span>
        ) : null}
      </span>
    ),
  },
  { key: 'status', header: 'Estado', align: 'right', cell: (c) => <StatusOf contact={c} /> },
];

/**
 * Mensagens do formulario de contacto de growup.gg. `general` (`contacts.manage`)
 * ve todos os assuntos menos a Direcao; `direction` (`contacts.direcao`) so a
 * Direcao (F-018). A API filtra na mesma; aqui so se escolhem os assuntos.
 */
export function ContactsSection({
  general = true,
  direction = false,
}: {
  general?: boolean;
  direction?: boolean;
}) {
  const subjects = contactSubjectSchema.options.filter((s) =>
    s === DIRECTION_SUBJECT ? direction : general,
  );
  // Por omissao so as novas: e o que ainda pede atencao.
  const [status, setStatus] = useState<ContactStatus | ''>('new');
  const [subject, setSubject] = useState<ContactSubject | ''>('');
  const [list, setList] = useState<AdminContactSummary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    if (subject) query.set('subject', subject);
    const qs = query.toString();
    setError(null);
    getJson(`/api/site/contacts${qs ? `?${qs}` : ''}`, z.array(adminContactSummarySchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [status, subject]);
  useEffect(load, [load]);

  if (selected) {
    return (
      <ContactDetail
        id={selected}
        onBack={() => {
          setSelected(null);
          load();
        }}
      />
    );
  }

  return (
    <Card headingLevel={2} title={general ? 'Mensagens de contacto' : 'Mensagens para a Direção'}>
      <div className="mb-4 flex flex-wrap gap-3">
        <Select
          aria-label="Estado"
          className="max-w-xs"
          value={status}
          onChange={(e) => setStatus(e.target.value as ContactStatus | '')}
        >
          <option value="">Todos os estados</option>
          {contactStatusSchema.options.map((s) => (
            <option key={s} value={s}>
              {CONTACT_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Assunto"
          className="max-w-xs"
          value={subject}
          onChange={(e) => setSubject(e.target.value as ContactSubject | '')}
        >
          <option value="">Todos os assuntos</option>
          {subjects.map((s) => (
            <option key={s} value={s}>
              {CONTACT_SUBJECT_LABELS[s]}
            </option>
          ))}
        </Select>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Table
        caption="Mensagens"
        columns={COLUMNS}
        rows={list ?? []}
        rowKey={(c) => c.id}
        empty={list ? 'Não há mensagens com estes filtros.' : 'A carregar…'}
        onRowClick={(c) => setSelected(c.id)}
      />
    </Card>
  );
}

function ContactDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [contact, setContact] = useState<AdminContact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [note, setNote] = useState('');
  // "Responder" abre a caixa: a resposta sai pelo email da plataforma.
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');

  useEffect(() => {
    getJson(`/api/site/contacts/${id}`, adminContactSchema)
      .then(setContact)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [id]);

  const act = async (run: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = await run();
      if (result) setContact(adminContactSchema.parse(result));
      setDone(message);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = (next: ContactStatus) =>
    act(
      () => send('PUT', `/api/site/contacts/${id}/status`, { status: next }),
      `Marcada como ${CONTACT_STATUS_LABELS[next].toLowerCase()}.`,
    );

  const addNote = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = await send('POST', `/api/site/contacts/${id}/notes`, { text: note });
      setContact(adminContactSchema.parse(result));
      setNote('');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = adminContactSchema.parse(
        await send('POST', `/api/site/contacts/${id}/replies`, { text: reply }),
      );
      setContact(result);
      setReply('');
      setReplying(false);
      if (result.replies.at(-1)?.mailedAt) setDone('Resposta enviada por email.');
      else setError('A resposta ficou guardada mas o email não saiu. Pode reenviá-la.');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await send('DELETE', `/api/site/contacts/${id}`);
      onBack();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Mensagens
        </Button>
      </div>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {contact ? (
        <div className="grid items-start gap-4 lg:grid-cols-[1fr_22rem]">
          <div className="flex flex-col gap-4">
            <Card
              headingLevel={2}
              title={CONTACT_SUBJECT_LABELS[contact.subject]}
              actions={<StatusOf contact={contact} />}
            >
              <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                <dt className="text-gu-ink-muted">De</dt>
                <dd>{contact.name}</dd>
                <dt className="text-gu-ink-muted">Email</dt>
                <dd>{contact.email}</dd>
                {contact.account ? (
                  <>
                    <dt className="text-gu-ink-muted">Conta</dt>
                    <dd>{contact.account.login}</dd>
                    <dt className="text-gu-ink-muted">Pode escrever como</dt>
                    <dd>
                      {contact.account.reasons.map((r) => DIRECTION_REASON_LABELS[r]).join(', ')}
                    </dd>
                  </>
                ) : null}
                <dt className="text-gu-ink-muted">Recebida</dt>
                <dd>{formatDateTime(contact.createdAt)}</dd>
                {contact.statusChangedAt ? (
                  <>
                    <dt className="text-gu-ink-muted">Estado</dt>
                    <dd>
                      {CONTACT_STATUS_LABELS[contact.status]} por {contact.statusChangedBy ?? '—'} a{' '}
                      {formatDateTime(contact.statusChangedAt)}
                    </dd>
                  </>
                ) : null}
              </dl>
              <p className="whitespace-pre-wrap break-words">{contact.message}</p>
            </Card>

            {replying || contact.replies.length > 0 ? (
              <Card headingLevel={3} title="Respostas">
                {contact.replies.length > 0 ? (
                  <ol className="mb-4 flex flex-col gap-3">
                    {contact.replies.map((r) => (
                      <li key={r.id} className="border-l-2 border-gu-accent pl-3">
                        <p className="flex flex-wrap items-center gap-2 text-caption text-gu-ink-muted">
                          <span>
                            {r.createdBy} · {formatDateTime(r.createdAt)}
                          </span>
                          {r.mailedAt === null ? (
                            <StatusBadge tone="warning">Não enviada</StatusBadge>
                          ) : null}
                        </p>
                        <p className="whitespace-pre-wrap break-words">{r.text}</p>
                        {r.mailedAt === null ? (
                          <Button
                            className="mt-2"
                            variant="secondary"
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              void act(
                                () =>
                                  send('POST', `/api/site/contacts/${id}/replies/${r.id}/resend`),
                                'Resposta enviada por email.',
                              )
                            }
                          >
                            Reenviar resposta
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                ) : null}
                {replying ? (
                  <>
                    <p className="mb-3 text-caption text-gu-ink-muted">
                      Sai por email para {contact.email}, com a mensagem original citada. Se
                      responderem, a resposta chega à caixa de contactos.
                    </p>
                    <Field label="Resposta">
                      <Textarea
                        rows={6}
                        maxLength={CONTACT_REPLY_MAX}
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                      />
                    </Field>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        disabled={busy || reply.trim() === ''}
                        onClick={() => void sendReply()}
                      >
                        Enviar resposta
                      </Button>
                      <Button variant="ghost" disabled={busy} onClick={() => setReplying(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </>
                ) : null}
              </Card>
            ) : null}

            <Card headingLevel={3} title="Notas internas">
              <p className="mb-3 text-caption text-gu-ink-muted">
                Só a equipa vê estas notas. Nunca são enviadas a quem escreveu.
              </p>
              {contact.notes.length > 0 ? (
                <ol className="mb-4 flex flex-col gap-3">
                  {contact.notes.map((n) => (
                    <li key={n.id} className="border-l-2 border-gu-line pl-3">
                      <p className="text-caption text-gu-ink-muted">
                        {n.createdBy} · {formatDateTime(n.createdAt)}
                      </p>
                      <p className="whitespace-pre-wrap break-words">{n.text}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mb-4 text-gu-ink-muted">Ainda não há notas.</p>
              )}
              <Field label="Nova nota">
                <Textarea
                  rows={3}
                  maxLength={CONTACT_NOTE_MAX}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
              <div className="mt-3">
                <Button
                  variant="secondary"
                  disabled={busy || note.trim() === ''}
                  onClick={() => void addNote()}
                >
                  Adicionar nota
                </Button>
              </div>
            </Card>
          </div>

          <Card headingLevel={3} title="Ações">
            <div className="flex flex-col gap-3">
              <Button disabled={busy || replying} onClick={() => setReplying(true)}>
                Responder
              </Button>
              {contact.status !== 'handled' ? (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void changeStatus('handled')}
                >
                  Marcar como tratada
                </Button>
              ) : null}
              {contact.status !== 'archived' ? (
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void changeStatus('archived')}
                >
                  Arquivar
                </Button>
              ) : null}
              {contact.status !== 'new' ? (
                <Button variant="ghost" disabled={busy} onClick={() => void changeStatus('new')}>
                  Voltar a nova
                </Button>
              ) : null}
              {contact.mailedAt === null ? (
                <>
                  <Notice tone="warning">
                    O email para a caixa de contactos não saiu quando a mensagem chegou.
                  </Notice>
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => send('POST', `/api/site/contacts/${id}/resend`),
                        'Email reenviado.',
                      )
                    }
                  >
                    Reenviar email
                  </Button>
                </>
              ) : null}
              {done ? <Notice tone="success">{done}</Notice> : null}
              <div className="mt-2 flex flex-col gap-2 border-t border-gu-line pt-3">
                <Checkbox
                  label="Apagar de vez esta mensagem"
                  checked={confirmDelete}
                  onChange={(e) => setConfirmDelete(e.target.checked)}
                />
                <Button
                  variant="danger"
                  disabled={busy || !confirmDelete}
                  onClick={() => void remove()}
                >
                  Apagar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      ) : error ? null : (
        <p className="text-gu-ink-muted">A carregar…</p>
      )}
    </div>
  );
}
